import { Platform, type TextStyle } from 'react-native';
import { colors } from './colors';
import { tokens } from './tokens';

// On web, expo-font registers each @font-face under the exact key passed to
// useFonts() (e.g. "Sora_800ExtraBold"), NOT a generic "Sora" family. So the
// web stacks MUST lead with the weight-suffixed registered name or the browser
// silently falls back to system fonts. The generic name + system fonts trail
// as fallbacks.
const SORA_BOLD_STACK = '"Sora_800ExtraBold", "Sora", "Helvetica Neue", sans-serif';
const SORA_STACK = '"Sora_600SemiBold", "Sora", "Helvetica Neue", sans-serif';
const MANROPE_REGULAR_STACK =
  '"Manrope_400Regular", "Manrope", "Inter", "Avenir Next", "SF Pro Display", "Helvetica Neue", sans-serif';
const MANROPE_MEDIUM_STACK =
  '"Manrope_500Medium", "Manrope", "Inter", "Avenir Next", "SF Pro Display", "Helvetica Neue", sans-serif';
const MONO_BOLD_STACK =
  '"GeistMono_600SemiBold", "Geist Mono", "SF Mono", Menlo, Consolas, monospace';
const MONO_STACK = '"GeistMono_400Regular", "Geist Mono", "SF Mono", Menlo, Consolas, monospace';

export const darkPrecisionFontFamilies = Platform.select({
  ios: {
    displayBold: 'Sora_800ExtraBold',
    display: 'Sora_600SemiBold',
    uiRegular: 'Manrope_400Regular',
    uiMedium: 'Manrope_500Medium',
    metricBold: 'GeistMono_600SemiBold',
    metric: 'GeistMono_400Regular',
  },
  android: {
    displayBold: 'Sora_800ExtraBold',
    display: 'Sora_600SemiBold',
    uiRegular: 'Manrope_400Regular',
    uiMedium: 'Manrope_500Medium',
    metricBold: 'GeistMono_600SemiBold',
    metric: 'GeistMono_400Regular',
  },
  web: {
    displayBold: SORA_BOLD_STACK,
    display: SORA_STACK,
    uiRegular: MANROPE_REGULAR_STACK,
    uiMedium: MANROPE_MEDIUM_STACK,
    metricBold: MONO_BOLD_STACK,
    metric: MONO_STACK,
  },
  default: {
    displayBold: 'Sora_800ExtraBold',
    display: 'Sora_600SemiBold',
    uiRegular: 'Manrope_400Regular',
    uiMedium: 'Manrope_500Medium',
    metricBold: 'GeistMono_600SemiBold',
    metric: 'GeistMono_400Regular',
  },
}) as {
  displayBold: string;
  display: string;
  uiRegular: string;
  uiMedium: string;
  metricBold: string;
  metric: string;
};

export const darkPrecisionTypography = {
  display: {
    fontFamily: darkPrecisionFontFamilies.displayBold,
    fontSize: 26,
    lineHeight: 32,
    fontWeight: tokens.fontWeight.extrabold,
    letterSpacing: -0.6,
    color: colors.text.primary,
  } as TextStyle,
  displayLg: {
    fontFamily: darkPrecisionFontFamilies.displayBold,
    fontSize: 36,
    lineHeight: 42,
    fontWeight: tokens.fontWeight.extrabold,
    letterSpacing: -1,
    color: colors.text.primary,
  } as TextStyle,
  heading: {
    fontFamily: darkPrecisionFontFamilies.display,
    fontSize: 16,
    lineHeight: 22,
    fontWeight: tokens.fontWeight.semibold,
    letterSpacing: -0.2,
    color: colors.text.primary,
  } as TextStyle,
  body: {
    fontFamily: darkPrecisionFontFamilies.uiRegular,
    fontSize: 14,
    lineHeight: 20,
    fontWeight: tokens.fontWeight.normal,
    color: colors.text.primary,
  } as TextStyle,
  bodyMuted: {
    fontFamily: darkPrecisionFontFamilies.uiRegular,
    fontSize: 14,
    lineHeight: 20,
    fontWeight: tokens.fontWeight.normal,
    color: colors.text.secondary,
  } as TextStyle,
  label: {
    fontFamily: darkPrecisionFontFamilies.uiMedium,
    fontSize: 11,
    lineHeight: 14,
    fontWeight: tokens.fontWeight.medium,
    letterSpacing: 1.32,
    textTransform: 'uppercase',
    color: colors.text.tertiary,
  } as TextStyle,
  metric: {
    fontFamily: darkPrecisionFontFamilies.metricBold,
    fontSize: 32,
    lineHeight: 36,
    fontWeight: tokens.fontWeight.semibold,
    letterSpacing: -0.4,
    color: colors.text.primary,
  } as TextStyle,
  metricSm: {
    fontFamily: darkPrecisionFontFamilies.metric,
    fontSize: 13,
    lineHeight: 16,
    fontWeight: tokens.fontWeight.normal,
    color: colors.text.secondary,
  } as TextStyle,
} as const;

export type DarkPrecisionTypographyVariant = keyof typeof darkPrecisionTypography;

// Shared form-field type tokens. React Native's TextInput does NOT inherit from
// Text, so the field primitives (Input, SearchInput, DatePickerInput.web)
// consume these directly to render the same Manrope UI family + one placeholder
// color as the rest of the app.
export const fieldFontFamily = darkPrecisionFontFamilies.uiRegular;
export const fieldPlaceholderColor = colors.text.tertiary;
// Branded focus state for fields — replaces the off-brand default browser
// outline with a single teal border (keeps WCAG focus visibility while staying
// on-palette, with no extra ring/glow that would read as a double border).
export const fieldFocusBorderColor = colors.border.focus;

// Unify the base scale on the dark-precision font system: Sora for display +
// headings, Manrope for body + UI, Geist Mono for numerics + code. The
// variant API is unchanged, so every existing `<Text variant=...>` call site
// inherits the premium type with no edits.
const dp = darkPrecisionFontFamilies;

export const typography = {
  h1: {
    fontSize: tokens.fontSize['5xl'],
    // Sora display needs ~1.2x headroom or iOS clips its tall ascenders.
    lineHeight: tokens.fontSize['5xl'] * tokens.lineHeight.snug,
    fontWeight: tokens.fontWeight.extrabold,
    fontFamily: dp.displayBold,
    color: colors.text.primary,
    letterSpacing: tokens.letterSpacing.tighter,
  } as TextStyle,

  h2: {
    fontSize: tokens.fontSize['4xl'],
    lineHeight: tokens.fontSize['4xl'] * tokens.lineHeight.snug,
    fontWeight: tokens.fontWeight.extrabold,
    fontFamily: dp.displayBold,
    color: colors.text.primary,
    letterSpacing: tokens.letterSpacing.tighter,
  } as TextStyle,

  h3: {
    fontSize: tokens.fontSize['3xl'],
    lineHeight: tokens.fontSize['3xl'] * tokens.lineHeight.snug,
    fontWeight: tokens.fontWeight.semibold,
    fontFamily: dp.display,
    color: colors.text.primary,
    letterSpacing: tokens.letterSpacing.tight,
  } as TextStyle,

  h4: {
    fontSize: tokens.fontSize['2xl'],
    lineHeight: tokens.fontSize['2xl'] * tokens.lineHeight.snug,
    fontWeight: tokens.fontWeight.semibold,
    fontFamily: dp.display,
    color: colors.text.primary,
  } as TextStyle,

  h5: {
    fontSize: tokens.fontSize.xl,
    lineHeight: tokens.fontSize.xl * tokens.lineHeight.normal,
    fontWeight: tokens.fontWeight.semibold,
    fontFamily: dp.display,
    color: colors.text.primary,
  } as TextStyle,

  h6: {
    fontSize: tokens.fontSize.lg,
    lineHeight: tokens.fontSize.lg * tokens.lineHeight.normal,
    fontWeight: tokens.fontWeight.semibold,
    fontFamily: dp.display,
    color: colors.text.primary,
  } as TextStyle,

  bodyLarge: {
    fontSize: tokens.fontSize.lg,
    lineHeight: tokens.fontSize.lg * tokens.lineHeight.relaxed,
    fontWeight: tokens.fontWeight.normal,
    fontFamily: dp.uiRegular,
    color: colors.text.primary,
  } as TextStyle,

  body: {
    fontSize: tokens.fontSize.base,
    lineHeight: tokens.fontSize.base * tokens.lineHeight.normal,
    fontWeight: tokens.fontWeight.normal,
    fontFamily: dp.uiRegular,
    color: colors.text.primary,
  } as TextStyle,

  bodySmall: {
    fontSize: tokens.fontSize.sm,
    lineHeight: tokens.fontSize.sm * tokens.lineHeight.normal,
    fontWeight: tokens.fontWeight.normal,
    fontFamily: dp.uiRegular,
    color: colors.text.secondary,
  } as TextStyle,

  caption: {
    fontSize: tokens.fontSize.xs,
    lineHeight: tokens.fontSize.xs * tokens.lineHeight.normal,
    fontWeight: tokens.fontWeight.normal,
    fontFamily: dp.uiRegular,
    color: colors.text.tertiary,
  } as TextStyle,

  button: {
    fontSize: tokens.fontSize.base,
    lineHeight: tokens.fontSize.base * tokens.lineHeight.tight,
    fontWeight: tokens.fontWeight.medium,
    fontFamily: dp.uiMedium,
    color: colors.text.primary,
    letterSpacing: tokens.letterSpacing.normal,
  } as TextStyle,

  buttonLarge: {
    fontSize: tokens.fontSize.md,
    lineHeight: tokens.fontSize.md * tokens.lineHeight.tight,
    fontWeight: tokens.fontWeight.medium,
    fontFamily: dp.uiMedium,
    color: colors.text.primary,
    letterSpacing: tokens.letterSpacing.normal,
  } as TextStyle,

  label: {
    fontSize: tokens.fontSize.sm,
    lineHeight: tokens.fontSize.sm * tokens.lineHeight.tight,
    fontWeight: tokens.fontWeight.medium,
    fontFamily: dp.uiMedium,
    letterSpacing: tokens.letterSpacing.normal,
    color: colors.text.secondary,
  } as TextStyle,

  code: {
    fontSize: tokens.fontSize.sm,
    lineHeight: tokens.fontSize.sm * tokens.lineHeight.normal,
    fontWeight: tokens.fontWeight.normal,
    fontFamily: dp.metric,
    color: colors.primary[500],
  } as TextStyle,

  overline: {
    fontSize: tokens.fontSize.xs,
    lineHeight: tokens.fontSize.xs * tokens.lineHeight.normal,
    fontWeight: tokens.fontWeight.semibold,
    fontFamily: dp.uiMedium,
    letterSpacing: tokens.letterSpacing.widest,
    textTransform: 'uppercase',
    color: colors.text.tertiary,
  } as TextStyle,

  metric: {
    fontSize: tokens.fontSize['4xl'],
    lineHeight: tokens.fontSize['4xl'] * tokens.lineHeight.tight,
    fontWeight: tokens.fontWeight.semibold,
    fontFamily: dp.metricBold,
    letterSpacing: tokens.letterSpacing.tighter,
    color: colors.text.primary,
  } as TextStyle,

  displayLarge: {
    fontSize: 56,
    lineHeight: 56 * tokens.lineHeight.snug,
    fontWeight: tokens.fontWeight.extrabold,
    fontFamily: dp.displayBold,
    letterSpacing: -1.4,
    color: colors.text.primary,
  } as TextStyle,
};

export type TypographyVariant = keyof typeof typography;

export const createTextStyle = (
  variant: TypographyVariant,
  overrides?: Partial<TextStyle>,
): TextStyle => {
  return {
    ...typography[variant],
    ...overrides,
  };
};
